import * as React from 'react';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CaretDown } from '@phosphor-icons/react';
import {
  pageMenuContentRecipe,
  pageMenuDotRecipe,
  pageMenuGroupRecipe,
  pageMenuItemRecipe,
  pageMenuSeparatorRecipe,
  pageMenuTriggerRecipe,
} from '../recipes/mobile.recipe';
import { Icon, type IconComponent } from './Icon';
import { Text } from './Text';

export interface PageMenuOption {
  value: string;
  label: string;
  icon: IconComponent;
}

export interface PageMenuProps {
  /** The page open now: its option's value. */
  value: string;
  onValueChange: (value: string) => void;
  /** The pages it moves between, in the order they are listed. */
  options: readonly PageMenuOption[];
}

/**
 * A page's title that opens the pages beside it, as the Functions tab's
 * dropdown (Figma 3-543): the open page's name with a caret, and a dark
 * menu of every page, each with its icon, the open one dotted. It replaced
 * a list of rows a member tapped through to reach a page, and back out of
 * to reach the next.
 */
export function PageMenu({ value, onValueChange, options }: PageMenuProps) {
  const open = options.find((option) => option.value === value);
  return (
    <MenuPrimitive.Root>
      {/* The heading holds the button, as a button may not hold a heading. */}
      <Text as="h1" variant="h2" className="min-w-0">
        <MenuPrimitive.Trigger className={pageMenuTriggerRecipe()}>
          <Text as="span" variant="inherit" truncate>
            {open?.label}
          </Text>
          <CaretDown aria-hidden />
        </MenuPrimitive.Trigger>
      </Text>
      <MenuPrimitive.Portal>
        {/* Under the title. The Figma lays it over the title, but there a
            mouse let go where it pressed lands on the page under the
            pointer, which picks it and shuts the menu at once. */}
        <MenuPrimitive.Content
          align="start"
          sideOffset={8}
          alignOffset={-12}
          collisionPadding={12}
          className={pageMenuContentRecipe()}
        >
          <MenuPrimitive.RadioGroup
            value={value}
            onValueChange={onValueChange}
            className={pageMenuGroupRecipe()}
          >
            {options.map((option, index) => (
              <React.Fragment key={option.value}>
                {index > 0 ? (
                  <MenuPrimitive.Separator
                    className={pageMenuSeparatorRecipe()}
                  />
                ) : null}
                <MenuPrimitive.RadioItem
                  value={option.value}
                  className={pageMenuItemRecipe()}
                >
                  <Icon icon={option.icon} size="md" />
                  {option.label}
                  <MenuPrimitive.ItemIndicator
                    className={pageMenuDotRecipe()}
                  />
                </MenuPrimitive.RadioItem>
              </React.Fragment>
            ))}
          </MenuPrimitive.RadioGroup>
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}
